import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { 
  Search, 
  MapPin, 
  Clock, 
  User, 
  Phone,
  Navigation,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Truck,
  Eye
} from 'lucide-react';
import BookingTrackingCard from '../components/ambulance/BookingTrackingCard';
import { useAuth } from '../contexts/AuthContext';

const BookingTrackingPage = ({ dashboardType = 'admin' }) => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [bookingId, setBookingId] = useState('');
  const [trackingBookingId, setTrackingBookingId] = useState(null);
  const [error, setError] = useState(null);

  // Check if user is a hospital user
  const isHospitalUser = () => {
    const hospitalRoles = ['HOSPITAL_USER', 'HOSPITAL_ADMIN', 'hospital'];
    return user?.role && hospitalRoles.includes(user.role);
  };

  // Redirect hospital users with bookingId to hospital dashboard
  useEffect(() => {
    const urlBookingId = searchParams.get('bookingId');
    const urlDirection = searchParams.get('direction') || '';
    if (urlBookingId && isHospitalUser()) {
      navigate(`/hospital?bookingId=${urlBookingId}${urlDirection ? `&direction=${urlDirection}` : ''}`, { replace: true });
    } else if (urlBookingId) {
      // For non-hospital users, auto-load the booking if bookingId is in URL
      setTrackingBookingId(urlBookingId);
    }
  }, [searchParams, navigate, user]);

  const handleTrackBooking = () => {
    if (!bookingId.trim()) {
      setError('Please enter a booking ID');
      return;
    }
    
    setError(null);
    setTrackingBookingId(bookingId.trim());
  };

  const handleCloseTracking = () => {
    setTrackingBookingId(null);
    setBookingId('');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Track Your Booking</h1>
              <p className="text-gray-600 mt-1">
                Enter your booking ID to track your ambulance in real-time
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-500">
                User: <span className="text-blue-600 font-medium">{user?.name || 'Guest'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!trackingBookingId ? (
          <div className="space-y-6">
            {/* Search Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Search className="w-5 h-5 text-blue-600" />
                  <span>Find Your Booking</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="bookingId" className="text-sm font-medium text-gray-700">
                    Booking ID
                  </label>
                  <Input
                    id="bookingId"
                    type="text"
                    placeholder="Enter your booking ID (e.g., 1391eac3-1169-424a-9803-9b2d939c184b)"
                    value={bookingId}
                    onChange={(e) => setBookingId(e.target.value)}
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-gray-500">
                    You can find your booking ID in the confirmation message or email.
                  </p>
                </div>

                {error && (
                  <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-md">
                    <AlertCircle className="w-4 h-4 text-red-600" />
                    <span className="text-sm text-red-800">{error}</span>
                  </div>
                )}

                <Button 
                  onClick={handleTrackBooking}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  disabled={!bookingId.trim()}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Track Booking
                </Button>
              </CardContent>
            </Card>

            {/* Help Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">How to Track Your Booking</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <h3 className="font-medium text-gray-900">What You Can Track</h3>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span>Real-time driver location</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span>Estimated arrival time</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span>Driver and ambulance details</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span>Status updates and history</span>
                      </li>
                    </ul>
                  </div>
                  <div className="space-y-3">
                    <h3 className="font-medium text-gray-900">Booking Statuses</h3>
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>
                        <span className="text-sm text-gray-600">Booking confirmed, awaiting dispatch</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge className="bg-blue-100 text-blue-800">Confirmed</Badge>
                        <span className="text-sm text-gray-600">Ambulance assigned and ready</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge className="bg-purple-100 text-purple-800">Dispatched</Badge>
                        <span className="text-sm text-gray-600">Ambulance is on the way</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge className="bg-orange-100 text-orange-800">In Transit</Badge>
                        <span className="text-sm text-gray-600">Ambulance is en route</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge className="bg-green-100 text-green-800">Arrived</Badge>
                        <span className="text-sm text-gray-600">Ambulance has arrived</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Recent Bookings */}
            {user && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Your Recent Bookings</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-8">
                    <Clock className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                    <p className="text-gray-600">No recent bookings found</p>
                    <p className="text-sm text-gray-500 mt-1">
                      Your recent bookings will appear here for quick access
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        ) : (
          <BookingTrackingCard 
            bookingId={trackingBookingId}
            journeyDirection={searchParams.get('direction') || 'OUTBOUND'}
            onClose={handleCloseTracking}
          />
        )}
      </div>
    </div>
  );
};

export default BookingTrackingPage;
